// Enum for rule types
const PostalCodeRule = {
  MAX_LENGTH: 'MAX_LENGTH',
  MAX_LENGTH_NO_GAPS: 'MAX_LENGTH_NO_GAPS',
  MAX_LENGTH_NUMERIC_NO_GAPS: 'MAX_LENGTH_NUMERIC_NO_GAPS',
  EXACT_LENGTH: 'EXACT_LENGTH',
  EXACT_LENGTH_NUMERIC: 'EXACT_LENGTH_NUMERIC',
  EXACT_LENGTH_NUMERIC_NO_GAPS: 'EXACT_LENGTH_NUMERIC_NO_GAPS',
  COUNTRY_SPECIFIC_FORMAT: 'COUNTRY_SPECIFIC_FORMAT'
};

// Rule implementations
const maxLengthNoGaps = (postalCode, length) => {
  return postalCode.length <= length && !/\s/.test(postalCode);
};

const maxLengthNumericNoGaps = (postalCode, length) => {
  return postalCode.length <= length && /^\d+$/.test(postalCode);
};

const exactLengthNumericNoGaps = (postalCode, length) => {
  return postalCode.length === length && /^\d+$/.test(postalCode);
};

const maxLength = (postalCode, length) => {
  return postalCode.length <= length;
};

const exactLength = (postalCode, length) => {
  return postalCode.length === length;
};

const countrySpecificFormat = (postalCode, format) => {
  return format.test(postalCode);
};

const exactLengthNumeric = (postalCode, length) => {
  return postalCode.length === length && /^[\d\s]+$/.test(postalCode);
};

// Country config
const countryRules = {
  AR: { length: 8, rule: PostalCodeRule.MAX_LENGTH_NO_GAPS, required: false },
  AT: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  AU: { length: 7, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  BE: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  BG: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  BR: { length: 9, rule: PostalCodeRule.EXACT_LENGTH, required: false },
  CA: {
    length: 7,
    rule: PostalCodeRule.COUNTRY_SPECIFIC_FORMAT,
    required: false,
    format: /^[A-Z]\d[A-Z] \d[A-Z]\d$/,
    error: "Must be 'ANA NAN'"
  },
  CH: { length: 6, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  CL: { length: 7, rule: PostalCodeRule.MAX_LENGTH_NO_GAPS, required: false },
  CN: { length: 6, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  CR: { length: 5, rule: PostalCodeRule.MAX_LENGTH, required: false },
  CY: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  CZ: {
    length: 6,
    rule: PostalCodeRule.COUNTRY_SPECIFIC_FORMAT,
    required: true,
    format: /^\d{3} \d{2}$/,
    error: "Must be 'NNN NN'"
  },
  DE: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  DK: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  DZ: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  ES: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  FI: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  FO: { length: 3, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  FR: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  GB: { length: 9, rule: PostalCodeRule.MAX_LENGTH, required: false },
  GR: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  HK: { length: 9, rule: PostalCodeRule.MAX_LENGTH, required: false },
  HR: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  HU: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  ID: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  IL: { length: 7, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  IN: { length: 6, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  IR: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  IS: { length: 3, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  IT: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  JP: { length: 8, rule: PostalCodeRule.MAX_LENGTH, required: false },
  KE: { length: 5, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  KP: { length: 7, rule: PostalCodeRule.MAX_LENGTH, required: true },
  KR: { length: 7, rule: PostalCodeRule.MAX_LENGTH_NO_GAPS, required: false },
  KW: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  KZ: { length: 6, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  LI: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  LS: { length: 3, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  LU: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  MA: { length: 5, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  MC: { length: 5, rule: PostalCodeRule.MAX_LENGTH, required: true },
  MO: { length: 6, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  MU: { length: 5, rule: PostalCodeRule.MAX_LENGTH, required: true },
  MX: { length: 5, rule: PostalCodeRule.MAX_LENGTH_NO_GAPS, required: false },
  MY: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  MZ: { length: 4, rule: PostalCodeRule.MAX_LENGTH, required: true },
  NG: { length: 6, rule: PostalCodeRule.MAX_LENGTH, required: false },
  NL: {
    length: 6,
    rule: PostalCodeRule.COUNTRY_SPECIFIC_FORMAT,
    required: false,
    format: /^\d{4} [A-Z]{2}$/,
    error: "Must be 'NNNN AA'"
  },
  NO: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  NP: { length: 6, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  NZ: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  PH: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  PL: {
    length: 6,
    rule: PostalCodeRule.COUNTRY_SPECIFIC_FORMAT,
    required: true,
    format: /^\d{2}-\d{3}$/,
    error: "Must be 'NN-NNN'"
  },
  PT: { length: 8, rule: PostalCodeRule.MAX_LENGTH_NO_GAPS, required: true },
  PY: { length: 4, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  RO: { length: 7, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  RS: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  RU: { length: 6, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  SA: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  SE: {
    length: 6,
    rule: PostalCodeRule.COUNTRY_SPECIFIC_FORMAT,
    required: true,
    format: /^\d{3} \d{2}$/,
    error: "Must be 'NNN NN'"
  },
  SG: { length: 6, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  SI: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  SK: { length: 6, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC, required: true },
  TD: { length: 5, required: true },
  TH: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  TN: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: true },
  TR: { length: 5, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  TT: { length: 5, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  TW: { length: 6, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  UA: { length: 6, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  US: { length: 10, rule: PostalCodeRule.MAX_LENGTH_NO_GAPS, required: false },
  VE: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false },
  VN: { length: 6, rule: PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS, required: false },
  ZA: { length: 4, rule: PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS, required: false }
};

export const validatePostalCode = ({ country, zipCode }) => {
  const errors = {};

  // country in format { label, value } if selected manually or string if autofilled
  const countryCode =
    // eslint-disable-next-line no-nested-ternary
    typeof country === 'string'
      ? country
      : Object.prototype.toString.call(country) === '[object Object]'
      ? country.value
      : undefined;

  if (!countryCode) {
    return errors;
  }

  const config = countryRules[countryCode];

  // If country not in rules or postal code empty, it's valid
  if (!config || !zipCode || zipCode.trim() === '') {
    return errors;
  }

  switch (config.rule) {
    case PostalCodeRule.MAX_LENGTH:
      if (!maxLength(zipCode, config.length)) {
        errors.zipCode = `Must be ${config.length} characters or less`;
        return errors;
      }
      break;
    case PostalCodeRule.MAX_LENGTH_NO_GAPS:
      if (!maxLengthNoGaps(zipCode, config.length)) {
        errors.zipCode = `Must be ${config.length} characters or less, no spaces`;
        return errors;
      }
      break;
    case PostalCodeRule.MAX_LENGTH_NUMERIC_NO_GAPS:
      if (!maxLengthNumericNoGaps(zipCode, config.length)) {
        errors.zipCode = `Must be ${config.length} digits or less, no spaces`;
        return errors;
      }
      break;
    case PostalCodeRule.EXACT_LENGTH:
      if (!exactLength(zipCode, config.length)) {
        errors.zipCode = `Must be exactly ${config.length} characters`;
        return errors;
      }
      break;
    case PostalCodeRule.EXACT_LENGTH_NUMERIC:
      if (!exactLengthNumeric(zipCode, config.length)) {
        errors.zipCode = `Must be exactly ${config.length} digits`;
        return errors;
      }
      break;
    case PostalCodeRule.EXACT_LENGTH_NUMERIC_NO_GAPS:
      if (!exactLengthNumericNoGaps(zipCode, config.length)) {
        errors.zipCode = `Must be exactly ${config.length} digits, no spaces`;
        return errors;
      }
      break;
    case PostalCodeRule.COUNTRY_SPECIFIC_FORMAT:
      if (!countrySpecificFormat(zipCode, config.format)) {
        errors.zipCode = config.error || 'Invalid postal code format';
        return errors;
      }
      break;
    default:
      return errors;
  }

  return errors;
};
